/**
 * The dive's full-screen fade layer: black over the whole shell while the
 * curtain is down, with the site caption (antenna, station, what the dish
 * follows). It fades over useDive.fadeMs (300 ms, 0 under reduced motion, so
 * the swap is instant), blocks input while opaque and is hidden and
 * pointer-events-none otherwise. It also ends a settled dive when the user
 * leaves the twin route or selects another asset without "Back to globe".
 * Mounted once by the Shell next to the Toaster (which stays above it).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { abandonDive, MAINTENANCE_PATH } from './controller';
import { useDive } from './store';

export function DiveOverlay() {
  const phase = useDive((s) => s.phase);
  const curtain = useDive((s) => s.curtain);
  const fadeMs = useDive((s) => s.fadeMs);
  const site = useDive((s) => s.site);
  const track = useDive((s) => s.track);
  const diveAssetId = useDive((s) => s.assetId);
  const { pathname } = useLocation();
  const selectedAssetId = useUI((s) => s.selection.assetId);

  useEffect(() => {
    if (phase !== 'twin') return;
    if (pathname !== MAINTENANCE_PATH && !pathname.startsWith(`${MAINTENANCE_PATH}/`)) abandonDive(`left the twin for ${pathname}`);
  }, [phase, pathname]);

  useEffect(() => {
    if (phase === 'twin' && diveAssetId && selectedAssetId !== diveAssetId) abandonDive(`the selection moved to ${selectedAssetId ?? 'nothing'}`);
  }, [phase, diveAssetId, selectedAssetId]);

  return (
    <div
      data-testid="dive-overlay"
      aria-hidden={!curtain}
      className={cn('fixed inset-0 z-40 flex items-center justify-center bg-black', curtain ? 'pointer-events-auto' : 'pointer-events-none')}
      style={{
        opacity: curtain ? 1 : 0,
        visibility: curtain ? 'visible' : 'hidden',
        // Visibility flips at the start of a fade in and at the end of a fade out.
        transition: `opacity ${fadeMs}ms ease-in-out, visibility 0ms linear ${curtain ? 0 : fadeMs}ms`,
      }}
    >
      {site && (
        <div data-testid="dive-caption" role="status" aria-live="polite" className="flex max-w-xl flex-col items-center gap-1.5 px-6 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-ink-3">
            {phase === 'returning' ? 'Back to the globe' : 'Diving to'}
          </div>
          <div className="font-display text-3xl font-semibold text-ink">{site.name}</div>
          {site.place && <div className="text-sm text-ink-2">{site.place}</div>}
          <div className="mt-1 font-mono text-xs text-accent">
            {site.shortName}
            {track && phase !== 'returning' ? `, ${track.label}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
